/**
 * Refinement Service
 * Handles text refinement requests using Claude API
 */

import type { RefineRequest, RefineResponse } from '@refinevoice/shared';
import { API_ENDPOINTS } from '@refinevoice/shared';
import { type ApiRequestOptions, ApiService } from './api.service';

/**
 * Refinement service for text processing
 */
export class RefinementService extends ApiService {
  /**
   * Refine text using Claude API
   *
   * @param text - Text to refine
   * @param customPrompt - Optional custom prompt for refinement style
   * @param options - Optional request options for timeout, retries, and cancellation
   * @returns Refined text response with original and refined text
   * @throws ApiError on request failure
   *
   * @example
   * ```typescript
   * const service = new RefinementService();
   * const result = await service.refine(
   *   'um so like basically we need to, uh, fix the bug',
   *   'Make this professional'
   * );
   * console.log(result.refinedText); // 'We need to fix the bug.'
   * ```
   */
  async refine(
    text: string,
    customPrompt?: string,
    options?: ApiRequestOptions
  ): Promise<RefineResponse> {
    if (!text || text.trim().length === 0) {
      throw new Error('Text cannot be empty');
    }

    const request: RefineRequest = {
      text: text.trim(),
      customPrompt,
    };

    return await this.request<RefineResponse>(API_ENDPOINTS.REFINE, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(request),
      retries: 0, // Don't retry refinement requests to preserve user quota
      ...options,
    });
  }

  /**
   * Refine a single word with context
   * Used for word-level refinement in the UI
   *
   * @param word - Word to refine
   * @param context - Context/prompt for refinement
   * @param options - Optional request options for timeout, retries, and cancellation
   * @returns Refined word as string
   * @throws ApiError on request failure
   *
   * @example
   * ```typescript
   * const service = new RefinementService();
   * const refined = await service.refineWord(
   *   'gonna',
   *   'Convert this informal word to formal English'
   * );
   * console.log(refined); // 'going to'
   * ```
   */
  async refineWord(word: string, context: string, options?: ApiRequestOptions): Promise<string> {
    if (!word || word.trim().length === 0) {
      throw new Error('Word cannot be empty');
    }

    if (!context || context.trim().length === 0) {
      throw new Error('Context cannot be empty');
    }

    const request: RefineRequest = {
      text: word.trim(),
      customPrompt: context.trim(),
    };

    const response = await this.request<RefineResponse>(API_ENDPOINTS.REFINE, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(request),
      retries: 0, // Don't retry refinement requests to preserve user quota
      ...options,
    });

    return response.refinedText;
  }

  /**
   * Refine text with a specific style preset
   *
   * @param text - Text to refine
   * @param style - Style preset (professional, casual, technical, concise, friendly)
   * @param options - Optional request options for timeout, retries, and cancellation
   * @returns Refined text response
   * @throws ApiError on request failure
   *
   * @example
   * ```typescript
   * const service = new RefinementService();
   * const result = await service.refineWithStyle(
   *   'hey so like we totally need to check this out',
   *   'professional'
   * );
   * console.log(result.refinedText); // 'We need to review this.'
   * ```
   */
  async refineWithStyle(
    text: string,
    style: 'professional' | 'casual' | 'technical' | 'concise' | 'friendly',
    options?: ApiRequestOptions
  ): Promise<RefineResponse> {
    if (!text || text.trim().length === 0) {
      throw new Error('Text cannot be empty');
    }

    const request: RefineRequest = {
      text: text.trim(),
      style,
    };

    return await this.request<RefineResponse>(API_ENDPOINTS.REFINE, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(request),
      retries: 0, // Don't retry refinement requests to preserve user quota
      ...options,
    });
  }

  /**
   * Batch refine multiple texts
   * Useful for refining multiple segments or paragraphs
   *
   * @param texts - Array of texts to refine
   * @param customPrompt - Optional custom prompt for all texts
   * @param options - Optional request options for timeout, retries, and cancellation
   * @returns Array of refined text responses
   * @throws ApiError on any request failure
   *
   * @example
   * ```typescript
   * const service = new RefinementService();
   * const texts = [
   *   'um so first thing is...',
   *   'and uh then we need to...',
   *   'like basically the last step is...'
   * ];
   * const results = await service.batchRefine(texts, 'Make professional');
   * results.forEach(r => console.log(r.refinedText));
   * ```
   */
  async batchRefine(
    texts: string[],
    customPrompt?: string,
    options?: ApiRequestOptions
  ): Promise<RefineResponse[]> {
    if (!texts || texts.length === 0) {
      throw new Error('Texts array cannot be empty');
    }

    // Filter out empty texts
    const validTexts = texts.filter((t) => t && t.trim().length > 0);

    if (validTexts.length === 0) {
      throw new Error('No valid texts to refine');
    }

    // Refine all texts in parallel
    const promises = validTexts.map((text) => this.refine(text, customPrompt, options));

    return await Promise.all(promises);
  }
}
